import { getSchema, type JSONContent } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";

export type RichTextDocument = JSONContent;

const ALLOWED_LINK_PROTOCOLS = new Set(["http:", "https:", "mailto:"]);
const LINK_ATTRIBUTE_KEYS = new Set(["href", "target", "rel", "class", "title"]);
const SAFE_LINK_TARGET = "_blank";
const SAFE_LINK_REL = "noopener noreferrer nofollow";

export function isAllowedRichTextLink(value: string): boolean {
  if (!value || value !== value.trim() || /[\u0000-\u001f\u007f]/.test(value)) return false;
  if (value.startsWith("/") || value.startsWith("#")) return false;
  const hasProtocol = /^[a-z][a-z\d+.-]*:/i.test(value);
  try {
    const url = new URL(hasProtocol ? value : `https://${value}`);
    if (!ALLOWED_LINK_PROTOCOLS.has(url.protocol)) return false;
    if (url.protocol === "mailto:") return url.pathname.length > 0;
    return Boolean(url.hostname) && !url.username && !url.password;
  } catch {
    return false;
  }
}

export const richTextEditorExtensions = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    link: {
      autolink: true,
      defaultProtocol: "https",
      isAllowedUri: (url) => isAllowedRichTextLink(url),
      openOnClick: false,
    },
  }),
];

const richTextSchema = getSchema(richTextEditorExtensions);

export function isRichTextDocument(value: unknown): value is RichTextDocument {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  try {
    richTextSchema.nodeFromJSON(value as JSONContent);
    return (value as Record<string, unknown>).type === "doc" && hasSafeLinkMarks(value);
  } catch {
    return false;
  }
}

function hasSafeLinkMarks(value: unknown): boolean {
  if (Array.isArray(value)) return value.every(hasSafeLinkMarks);
  if (!value || typeof value !== "object") return true;
  const record = value as Record<string, unknown>;
  if (record.marks !== undefined) {
    if (!Array.isArray(record.marks)) return false;
    for (const mark of record.marks) {
      if (!mark || typeof mark !== "object" || Array.isArray(mark)) return false;
      const markRecord = mark as Record<string, unknown>;
      if (markRecord.type !== "link") continue;
      if (Object.keys(markRecord).some((key) => key !== "type" && key !== "attrs")) return false;
      const attrs = markRecord.attrs;
      if (!attrs || typeof attrs !== "object" || Array.isArray(attrs)) return false;
      const attributeRecord = attrs as Record<string, unknown>;
      if (Object.keys(attributeRecord).some((key) => !LINK_ATTRIBUTE_KEYS.has(key))) return false;
      if (
        typeof attributeRecord.href !== "string"
        || !isAllowedRichTextLink(attributeRecord.href)
      ) return false;
      if (
        attributeRecord.target !== undefined
        && attributeRecord.target !== SAFE_LINK_TARGET
      ) return false;
      if (
        attributeRecord.rel !== undefined
        && attributeRecord.rel !== SAFE_LINK_REL
      ) return false;
      if (attributeRecord.class !== undefined && attributeRecord.class !== null) return false;
      if (
        attributeRecord.title !== undefined
        && attributeRecord.title !== null
        && typeof attributeRecord.title !== "string"
      ) return false;
    }
  }
  return Object.values(record).every(hasSafeLinkMarks);
}

export function cloneRichTextDocument(value: RichTextDocument): RichTextDocument {
  return JSON.parse(JSON.stringify(value)) as RichTextDocument;
}

export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record).sort().map((key) =>
    `${JSON.stringify(key)}:${canonicalJson(record[key])}`).join(",")}}`;
}
